import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { AlertTriangleIcon, CheckCircleBrokenIcon, InfoCircleIcon, PlaceholderIcon, XCloseIcon } from '../Icons';
import { FeaturedIcon } from '../Misc/FeaturedIcon';
import { IconSize, Variant as FeaturedIconVariant } from '../Misc/FeaturedIcon/types';
import { BaseColor, theme } from '../theme';
import { renderWithTooltip } from '../Tooltip';
import { LabelWithHint } from '../Typography/LabelWithHint';
import { StyledActionButtonsWrapper, StyledAlert, StyledCloseButton, StyledContent, StyledContentWrapper, StyledSupportingText, StyledText } from './styled';
import { AlertVariant } from './types';
const AlertIcon = ({ variant, icon, tone })=>{
    return /*#__PURE__*/ _jsx(FeaturedIcon, {
        variant: variant,
        icon: icon,
        size: IconSize.m,
        tone: tone
    });
};
const IconMap = {
    default: /*#__PURE__*/ _jsx(AlertIcon, {
        variant: FeaturedIconVariant.modern,
        icon: /*#__PURE__*/ _jsx(PlaceholderIcon, {
            type: "default"
        }),
        tone: BaseColor.gray
    }),
    brand: /*#__PURE__*/ _jsx(AlertIcon, {
        icon: /*#__PURE__*/ _jsx(AlertTriangleIcon, {
            type: "default"
        }),
        tone: BaseColor.primary
    }),
    gray: /*#__PURE__*/ _jsx(AlertIcon, {
        icon: /*#__PURE__*/ _jsx(AlertTriangleIcon, {
            type: "default"
        }),
        tone: BaseColor.gray
    }),
    warning: /*#__PURE__*/ _jsx(AlertIcon, {
        icon: /*#__PURE__*/ _jsx(AlertTriangleIcon, {
            type: "default"
        }),
        tone: BaseColor.warning
    }),
    error: /*#__PURE__*/ _jsx(AlertIcon, {
        icon: /*#__PURE__*/ _jsx(AlertTriangleIcon, {
            type: "default"
        }),
        tone: BaseColor.error
    }),
    success: /*#__PURE__*/ _jsx(AlertIcon, {
        icon: /*#__PURE__*/ _jsx(CheckCircleBrokenIcon, {
            type: "default"
        }),
        tone: BaseColor.success
    }),
    info: /*#__PURE__*/ _jsx(AlertIcon, {
        icon: /*#__PURE__*/ _jsx(InfoCircleIcon, {
            type: "default"
        }),
        tone: BaseColor.gray
    })
};
export function Alert({ variant = AlertVariant.warning, title, details, banner, children, icon, iconVariant = FeaturedIconVariant.gradient, showIcon = true, closable, onClose, tooltip, hint, actions, 'data-test-id': dataTestId = 'alert', ...restProps }) {
    iconVariant = iconVariant || (banner ? FeaturedIconVariant.light : FeaturedIconVariant.gradient);
    const alertIcon = icon !== undefined ? /*#__PURE__*/ _jsx(AlertIcon, {
        icon: icon,
        variant: iconVariant
    }) : IconMap[variant];
    let alertUI = /*#__PURE__*/ _jsxs(StyledAlert, {
        direction: "horizontal",
        alignY: "top",
        $banner: banner,
        role: "alert",
        ...restProps,
        "data-test-id": dataTestId,
        "data-state": variant,
        children: [
            showIcon && /*#__PURE__*/ _jsx("span", {
                "aria-label": String(variant),
                children: alertIcon
            }),
            /*#__PURE__*/ _jsxs(StyledContentWrapper, {
                grow: "1",
                $banner: banner,
                children: [
                    children ? children : /*#__PURE__*/ _jsx(_Fragment, {
                        children: /*#__PURE__*/ _jsxs(StyledContent, {
                            grow: "1",
                            $banner: banner,
                            children: [
                                title && /*#__PURE__*/ _jsx(StyledText, {
                                    children: title
                                }),
                                details && /*#__PURE__*/ _jsx(StyledSupportingText, {
                                    tooltip: !tooltip,
                                    tooltipProps: {
                                        style: {
                                            maxWidth: '500px'
                                        }
                                    },
                                    children: details
                                })
                            ]
                        })
                    }),
                    actions && /*#__PURE__*/ _jsx(StyledActionButtonsWrapper, {
                        direction: "horizontal",
                        alignY: "center",
                        children: actions
                    })
                ]
            }),
            closable && /*#__PURE__*/ _jsx(StyledCloseButton, {
                variant: "plain",
                icon: /*#__PURE__*/ _jsx(XCloseIcon, {
                    type: "default",
                    color: theme.colors.gray400
                }),
                onClick: onClose,
                "aria-label": "Close alert",
                $banner: banner,
                $isSingleLine: [
                    title,
                    details,
                    children,
                    actions
                ].filter(Boolean).length === 1,
                "data-test-id": "alert-close-button"
            })
        ]
    });
    if (tooltip) {
        alertUI = /*#__PURE__*/ _jsx(_Fragment, {
            children: renderWithTooltip(alertUI, tooltip)
        });
    }
    if (hint) {
        alertUI = /*#__PURE__*/ _jsx(LabelWithHint, {
            hint: hint,
            children: alertUI
        });
    }
    return alertUI;
}
